/**
 * Candidate values for one account block's property filter (DAT-9223).
 *
 * | field | source |
 * |---|---|
 * | SFDC_STAGE | the fixed bucket set (accountPipelineFilter.STAGE_BUCKETS) — no request |
 * | ACCOUNT_NAME / OPP_NAME | ListAccountPipelineFilterOptions: substring search + keyset paging, over exactly this block's scope (window, industry, block, score floor) |
 * | ACCOUNT_HIERARCHY | ListHierarchyNodes, fetched once for the page and handed in ({@link useHierarchyNodeOptions}) |
 *
 * Cloudscape behaviour this is built on (read from the installed source, see memory
 * reference_cloudscape_select_load_items): the input already waits 200ms before `onLoadItems`,
 * the same filtering text does not fire twice, "load more" fires only while the status is
 * `pending`, and Retry is `{ firstPage: false, samePage: true }` — the failed batch again, not the
 * next one. On top of that:
 *  - 300ms of our own debounce, the same as the Tagged page's filter, so the two feel alike;
 *  - a per-field sequence number, so only the latest request for a field lands — bumped when a
 *    new term is typed, not only when its debounced request goes, so a reply for the previous
 *    term cannot land in between;
 *  - a "first page" for the term already being walked continues the walk instead of restarting
 *    it. Cloudscape asks for a first page on its own whenever its client-side-filtered list is
 *    empty and the status is `pending` — e.g. `acme ` with a trailing space, which the service
 *    matches as `acme` but Cloudscape filters with the space — and restarting would fetch page
 *    one forever;
 *  - batches ACCUMULATE per field (deduplicated): the options of a value already in a token
 *    must stay present across searches;
 *  - a scope change (window, industry, score floor) empties everything and cancels the pending
 *    timer — a timer armed under the old scope would otherwise fetch it and land old-scope
 *    names — then reloads the fields that had been opened, because Cloudscape will not fire again
 *    for the same filtering text.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { PropertyFilterProps } from '@amzn/awsui-components-react';
import {
  getIvtClient,
  ListAccountPipelineFilterOptionsCommand,
  type AccountPipelineFilterField,
} from '../../client/IvtHubClient';
import { fetchHierarchyNodes } from '../ConfigurationPage/hierarchyNodeCatalog';
import { hierarchyLabel, hierarchyValue, STAGE_BUCKETS } from './accountPipelineFilter';
import type { WindowRange } from './components/windowPresets';

type Option = PropertyFilterProps.FilteringOption;
type Status = PropertyFilterProps.StatusType;

const SEARCHED_FIELDS: ReadonlySet<string> = new Set(['ACCOUNT_NAME', 'OPP_NAME']);
const DEBOUNCE_MS = 300;
const PAGE_SIZE = 50;
/**
 * The search term is cut to this many characters before it is sent. It travels in the query
 * string too, under the same 2 KB WAF limit as the filter (100 CJK characters = 900 bytes), and
 * a shorter substring only widens the match. Its paging token carries the last value seen, so a
 * very long matching name adds to it; names that long (over ~150 CJK characters) are not seen.
 */
export const MAX_SEARCH_CHARS = 100;

/** The term as the service will match it: trimmed, and cut by code point so no surrogate half is
 *  left for the client's URI encoder to throw on. */
export function searchTermOf(text: string): string {
  return Array.from(text.trim()).slice(0, MAX_SEARCH_CHARS).join('');
}

const STAGE_OPTIONS: readonly Option[] = STAGE_BUCKETS.map((b) => ({
  propertyKey: 'SFDC_STAGE',
  value: b.value,
  label: b.label,
}));

export interface HierarchyNodeOptions {
  options: readonly Option[];
  status: Status;
  reload: () => void;
}

/**
 * ListHierarchyNodes as ACCOUNT_HIERARCHY options, `<level>:<name>`. Once per page: the catalog
 * is the whole SFDC hierarchy, not scoped by industry or block, so both blocks share it.
 */
export function useHierarchyNodeOptions(enabled: boolean): HierarchyNodeOptions {
  const [options, setOptions] = useState<readonly Option[]>([]);
  const [status, setStatus] = useState<Status>('pending');
  const [attempt, setAttempt] = useState(0);
  const loaded = useRef(false);

  useEffect(() => {
    if (!enabled || loaded.current) return;
    let cancelled = false;
    setStatus('loading');
    fetchHierarchyNodes()
      .then((catalog) => {
        if (cancelled) return;
        loaded.current = true;
        setOptions(
          catalog.options.map((o) => {
            const value = hierarchyValue(o.propertyKey ?? '', o.value ?? '');
            return { propertyKey: 'ACCOUNT_HIERARCHY', value, label: hierarchyLabel(value) };
          }),
        );
        setStatus('finished');
      })
      .catch(() => {
        if (!cancelled) setStatus('error');
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, attempt]);

  const reload = useCallback(() => setAttempt((a) => a + 1), []);
  return { options, status, reload };
}

export interface AccountPipelineFilterOptions {
  filteringOptions: Option[];
  status: Status;
  onLoadItems: (e: { detail: PropertyFilterProps.LoadItemsDetail }) => void;
}

export function useAccountPipelineFilterOptions(
  range: WindowRange,
  industry: string | null,
  lighthouse: boolean,
  minScore: number | null,
  hierarchy: HierarchyNodeOptions,
  enabled = true,
): AccountPipelineFilterOptions {
  const { startDate, endDate } = range;
  const [names, setNames] = useState<Record<string, Option[]>>({});
  const [nameStatus, setNameStatus] = useState<Record<string, Status>>({});
  /** The field whose dropdown is open — its status is the control's. */
  const [active, setActive] = useState<string | undefined>(undefined);

  const tokens = useRef<Record<string, string | undefined>>({});
  /** `nameStatus` as of now, for the event handler (state is a render behind). */
  const statuses = useRef<Record<string, Status>>({});
  const setStatusOf = useCallback((field: string, value: Status) => {
    statuses.current[field] = value;
    setNameStatus((s) => ({ ...s, [field]: value }));
  }, []);
  const terms = useRef<Record<string, string | undefined>>({});
  const seqs = useRef<Record<string, number>>({});
  /** Per field: moving to another field within the debounce must not cancel this one's request. */
  const timers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const clearTimers = () => {
    for (const t of Object.values(timers.current)) clearTimeout(t);
    timers.current = {};
  };

  const live = enabled && industry !== null;

  const fetchBatch = useCallback(
    async (field: string, searchTerm: string, firstPage: boolean) => {
      if (!live || industry === null) return;
      const seq = (seqs.current[field] = (seqs.current[field] ?? 0) + 1);
      terms.current[field] = searchTerm;
      if (firstPage) tokens.current[field] = undefined;
      const token = tokens.current[field];
      setStatusOf(field, 'loading');
      try {
        const out = await getIvtClient().send(
          new ListAccountPipelineFilterOptionsCommand({
            startDate,
            endDate,
            industries: [industry],
            lighthouse,
            field: field as AccountPipelineFilterField,
            ...(minScore !== null && { minRelevanceScore: minScore }),
            ...(searchTerm !== '' && { searchTerm }),
            ...(token !== undefined && { nextToken: token }),
            maxResults: PAGE_SIZE,
          }),
        );
        if (seq !== seqs.current[field]) return;
        // Advanced only on success, so a Retry replays the batch that failed.
        tokens.current[field] = out.nextToken ?? undefined;
        const batch = (out.items ?? [])
          .filter((o) => !!o.value)
          .map((o) => ({ propertyKey: field, value: o.value as string, label: o.label ?? o.value }));
        setNames((prev) => {
          const have = prev[field] ?? [];
          const seen = new Set(have.map((o) => o.value));
          const added = batch.filter((o) => !seen.has(o.value));
          return added.length ? { ...prev, [field]: [...have, ...added] } : prev;
        });
        setStatusOf(field, out.nextToken ? 'pending' : 'finished');
      } catch {
        if (seq === seqs.current[field]) setStatusOf(field, 'error');
      }
    },
    [live, industry, startDate, endDate, lighthouse, minScore, setStatusOf],
  );

  // A new scope: nothing loaded so far describes it.
  useEffect(() => {
    clearTimers();
    for (const f of Object.keys(seqs.current)) seqs.current[f]++;
    tokens.current = {};
    statuses.current = {};
    setNames({});
    setNameStatus({});
    if (!live) return;
    for (const f of Object.keys(terms.current)) void fetchBatch(f, terms.current[f] ?? '', true);
    // fetchBatch is recreated by exactly these.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live, industry, startDate, endDate, lighthouse, minScore]);

  useEffect(
    () => () => {
      clearTimers();
    },
    [],
  );

  const { status: hierarchyStatus, reload: reloadHierarchy } = hierarchy;
  const onLoadItems = useCallback(
    ({ detail }: { detail: PropertyFilterProps.LoadItemsDetail }) => {
      const field = detail.filteringProperty?.key;
      setActive(field);
      if (field === 'ACCOUNT_HIERARCHY') {
        if (detail.samePage && hierarchyStatus === 'error') reloadHierarchy();
        return;
      }
      if (!field || !SEARCHED_FIELDS.has(field)) return;
      if (detail.firstPage) {
        const term = searchTermOf(detail.filteringText ?? '');
        if (term === terms.current[field]) {
          const now = statuses.current[field];
          // Already on its way (or its timer is armed).
          if (now === 'loading') return;
          // Cloudscape's own "first page" on an empty filtered list: walk on, see the header.
          if (now === 'pending') return void fetchBatch(field, term, false);
          // Every batch for this term is in hand.
          if (now === 'finished') return;
        }
        clearTimeout(timers.current[field]);
        // Recorded now, so a scope change before the timer fires reloads THIS term, and bumped
        // now, so a reply for the previous term is dropped.
        terms.current[field] = term;
        seqs.current[field] = (seqs.current[field] ?? 0) + 1;
        // Loading from the keystroke, not from when the debounce lets the request go.
        setStatusOf(field, 'loading');
        timers.current[field] = setTimeout(() => void fetchBatch(field, term, true), DEBOUNCE_MS);
      } else {
        // Load more, or Retry (`samePage`): the token is still the failed batch's.
        void fetchBatch(field, terms.current[field] ?? '', false);
      }
    },
    [fetchBatch, hierarchyStatus, reloadHierarchy, setStatusOf],
  );

  const status: Status =
    active === 'ACCOUNT_HIERARCHY'
      ? hierarchy.status
      : active !== undefined && SEARCHED_FIELDS.has(active)
        ? (nameStatus[active] ?? 'pending')
        : 'finished';

  return {
    filteringOptions: [
      ...STAGE_OPTIONS,
      ...(names.ACCOUNT_NAME ?? []),
      ...(names.OPP_NAME ?? []),
      ...hierarchy.options,
    ],
    status,
    onLoadItems,
  };
}
